import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// الحالات — النموذج المُبسّط اللي طلبه المشغّل:
//   • كود الحاله
//   • وصف
//   • المبلغ المطلوب (الهدف)
//   • توجل «عاجل / غير عاجل» — لو مفتوح بيظهر تاج «عاجله» على الحاله
// الجدول ف Supabase اسمه urgent_cases (احتفظنا بالاسم عشان مانكسرش
// FK بتاعت الـ donations) بس ف الـ UI بنسميه «الحالات».

type CaseRow = {
  id: string;
  title_ar: string;
  title_en?: string | null;
  case_code?: string | null;
  description_ar: string;
  description_en?: string | null;
  goal_egp: number | null;
  raised_egp: number | null;
  active: boolean;
  featured: boolean;   // ← ده اللي بنعرضه للأدمن كـ "عاجل"
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
};

type Draft = {
  id?: string;
  case_code: string;
  description_ar: string;
  description_en: string;
  goal_egp: number | null;
  active: boolean;
  featured: boolean;
};

const emptyDraft: Draft = {
  case_code: '',
  description_ar: '',
  description_en: '',
  goal_egp: null,
  active: true,
  featured: false,
};

export default function HomeCasesPage() {
  const [rows, setRows] = useState<CaseRow[]>([]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => { void load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('urgent_cases')
      .select('*')
      .order('created_at', { ascending: false });
    if (err) setError(err.message);
    else setRows((data ?? []) as CaseRow[]);
    setLoading(false);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    // Server-side columns require Arabic title (NOT NULL). Use the
    // description as the title when the operator hasn't typed one so
    // the row lands cleanly. Derive the English title the same way.
    const derivedTitle = editing.description_ar.trim().split('\n')[0].slice(0, 120) || 'حاله';
    const derivedTitleEn = editing.description_en.trim().split('\n')[0].slice(0, 120);
    const payload = {
      case_code: editing.case_code.trim() || null,
      title_ar: derivedTitle,
      title_en: derivedTitleEn || null,
      description_ar: editing.description_ar,
      description_en: editing.description_en.trim() || null,
      goal_egp: editing.goal_egp,
      active: editing.active,
      featured: editing.featured,
      updated_at: new Date().toISOString(),
    };
    const { error: err } = creating
      ? await supabase.from('urgent_cases').insert(payload)
      : await supabase.from('urgent_cases').update(payload).eq('id', editing.id!);
    if (err) return setError(err.message);
    setEditing(null); setCreating(false); await load();
  };

  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'حذف الحالة دي؟',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    const { error: err } = await supabase.from('urgent_cases').delete().eq('id', id);
    if (err) setError(err.message); else await load();
  };

  const startEdit = (c: CaseRow) => {
    setCreating(false);
    setEditing({
      id: c.id,
      case_code: c.case_code ?? '',
      description_ar: c.description_ar ?? '',
      description_en: c.description_en ?? '',
      goal_egp: c.goal_egp,
      active: c.active,
      featured: c.featured,
    });
  };

  return (
    <>
      <PageHeader
        eyebrow="الرئيسية"
        title="الحالات"
        subtitle="أدخل كود ووصف ومبلغ الحاله. فعّل «عاجله» يظهر تاج أحمر عليها في التطبيق."
        actions={<button className="btn" onClick={() => { setCreating(true); setEditing({ ...emptyDraft }); }}>+ حالة جديدة</button>}
      />
      {error && <div className="error-inline">{error}</div>}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : rows.length === 0 ? (
        <div className="card empty">مفيش حالات — ابدأ بإضافة حالة.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>الكود</th>
              <th>الوصف</th>
              <th>المبلغ (جنيه)</th>
              <th>عاجله؟</th>
              <th>الحالة</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td style={{ direction: 'ltr', fontFamily: 'monospace' }}>{c.case_code ?? '—'}</td>
                <td style={{ maxWidth: 320 }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{c.description_ar}</div>
                </td>
                <td>{c.goal_egp != null ? c.goal_egp.toLocaleString('ar-EG') : '—'}</td>
                <td>
                  {c.featured ? (
                    <span className="badge" style={{ background: '#d94a4a', color: '#fff' }}>عاجله</span>
                  ) : (
                    <span className="badge badge-muted">عاديه</span>
                  )}
                </td>
                <td>
                  {c.completed_at ? (
                    <span className="badge badge-success">تم التجميع</span>
                  ) : c.active ? (
                    <span className="badge" style={{ background: '#e6f4f1', color: '#065f60' }}>ظاهره</span>
                  ) : (
                    <span className="badge badge-muted">مخفيه</span>
                  )}
                </td>
                <td style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-small btn-secondary" onClick={() => startEdit(c)}>تعديل</button>
                  <button className="btn btn-small btn-danger" onClick={() => void remove(c.id)}>حذف</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{creating ? 'حالة جديدة' : `تعديل الحاله: ${editing.case_code || ''}`}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>إغلاق</button>
            </div>
            <form onSubmit={save}>
              <div className="field">
                <label>كود الحاله</label>
                <input
                  required
                  value={editing.case_code}
                  onChange={(e) => setEditing({ ...editing, case_code: e.target.value })}
                  placeholder="مثلاً 87009"
                  style={{ direction: 'ltr' }}
                />
              </div>
              <div className="row">
                <div className="field">
                  <label>الوصف (عربي)</label>
                  <textarea
                    required
                    rows={4}
                    value={editing.description_ar}
                    onChange={(e) => setEditing({ ...editing, description_ar: e.target.value })}
                    placeholder="اكتب وصف الحاله والاحتياج"
                  />
                </div>
                <div className="field">
                  <label>Description (English)</label>
                  <textarea
                    rows={4}
                    value={editing.description_en}
                    onChange={(e) => setEditing({ ...editing, description_en: e.target.value })}
                    placeholder="Description shown when the app is in English"
                  />
                </div>
              </div>
              <div className="field">
                <label>المبلغ المطلوب (جنيه)</label>
                <input
                  type="number"
                  min={0}
                  value={editing.goal_egp ?? ''}
                  onChange={(e) => setEditing({ ...editing, goal_egp: e.target.value ? Number(e.target.value) : null })}
                  style={{ direction: 'ltr' }}
                />
              </div>
              <div className="row">
                <div className="field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={editing.featured}
                      onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                    />
                    عاجله (يظهر تاج أحمر «عاجله» على الحاله)
                  </label>
                </div>
                <div className="field">
                  <label>الحالة</label>
                  <select value={editing.active ? 'y' : 'n'} onChange={(e) => setEditing({ ...editing, active: e.target.value === 'y' })}>
                    <option value="y">ظاهره</option>
                    <option value="n">مخفيه</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>إلغاء</button>
                <button type="submit" className="btn">{creating ? 'نشر' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
